import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../services/api';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from './AdminHeader';
import '../../css/adminherobanners.css';
import { Plus, Pencil, Trash2, Loader2, AlertCircle, ImagePlus, Upload, X } from 'lucide-react';
import { BannerThumbnail } from '../../components/admin/BannerThumbnail';
// Table.jsx lives at src/components/Table.jsx
import { Table } from '../../components/Table';
import { useToast } from '../../contexts/ToastContext';

// resolve stored /uploads/... paths against the API's origin
const API_ORIGIN = (
  process.env.REACT_APP_API_URL ||
  (api?.defaults?.baseURL || '').replace(/\/api\/?$/, '')
).replace(/\/$/, '');

const resolveMediaUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path) || path.startsWith('blob:')) return path;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_ORIGIN}${cleanPath}`;
};

const emptyForm = {
  title: '',
  subtitle: '',
  ctaPrimaryLabel: '',
  order: 0,
  isActive: true
};

const AdminHeroBannersPage = () => {
  const { confirm } = useToast();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const imageInputRef = useRef(null);

  useEffect(() => {
    loadBanners();
  }, []);

  // clean up any local object URL so we don't leak memory
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/hero-banners');
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load banners');
      console.error(err);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingBanner && !imageFile) {
      setError('Please select a banner image to upload');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const body = new FormData();
      body.append('title', formData.title);
      body.append('subtitle', formData.subtitle);
      body.append('ctaPrimaryLabel', formData.ctaPrimaryLabel);
      body.append('order', formData.order);
      body.append('isActive', formData.isActive);
      if (imageFile) body.append('image', imageFile);

      if (editingBanner) {
        await api.put(`/hero-banners/${editingBanner._id}`, body, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.post('/hero-banners', body, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      await loadBanners();
      closeModal();
    } catch (err) {
      setError('Failed to save banner');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete this banner?',
      message: 'This hero banner will be removed from the homepage.',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      await api.delete(`/hero-banners/${id}`);
      await loadBanners();
    } catch (err) {
      setError('Failed to delete banner');
      console.error(err);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/hero-banners/${id}`, { isActive: !currentStatus });
      await loadBanners();
    } catch (err) {
      setError('Failed to update banner status');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setImageFile(null);
    setImagePreview('');
    setEditingBanner(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const openModal = (banner = null) => {
    setError('');
    if (banner) {
      setFormData({
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        ctaPrimaryLabel: banner.ctaPrimaryLabel || '',
        order: banner.order || 0,
        isActive: banner.isActive
      });
      setImagePreview(resolveMediaUrl(banner.image));
      setImageFile(null);
      setEditingBanner(banner);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Order',
        accessor: 'order',
        Cell: ({ value }) => <span className="admin-order-badge">#{value ?? 0}</span>
      },
      { Header: 'Title', accessor: 'title' },
      { Header: 'Subtitle', accessor: 'subtitle', Cell: ({ value }) => value || '—' },
      {
        Header: 'Image',
        id: 'image',
        disableSortBy: true,
        Cell: ({ row }) => (
          <BannerThumbnail
            src={resolveMediaUrl(row.original.image)}
            alt={row.original.title || 'Hero Banner'}
            title={row.original.title}
          />
        )
      },
      {
        Header: 'Status',
        id: 'status',
        Cell: ({ row }) => {
          const banner = row.original;
          return (
            <button
              className={`admin-status-pill ${banner.isActive ? 'active' : 'inactive'}`}
              onClick={() => toggleStatus(banner._id, banner.isActive)}
              title="Click to toggle status"
            >
              <span className="status-dot" />
              {banner.isActive ? 'Active' : 'Inactive'}
            </button>
          );
        }
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="admin-action-group">
            <button
              className="admin-btn-edit"
              onClick={() => openModal(row.original)}
              title="Edit Banner"
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              className="admin-btn-delete"
              onClick={() => handleDelete(row.original._id)}
              title="Delete Banner"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        )
      }
    ],
    []
  );

  // purely visual drag highlight for the upload dropzone (no state involved)
  const dragOn = (e) => e.currentTarget.classList.add('is-drag');
  const dragOff = (e) => e.currentTarget.classList.remove('is-drag');

  const pageHeading = (withAction) => (
    <div className="ahb-heading">
      <div>
        <h1 className="ahb-title">Hero Banners</h1>
        <p className="ahb-subtitle">Large showcase banners at the top of the homepage.</p>
      </div>
      {withAction && (
        <div className="ahb-heading-actions">
          <button type="button" className="ahb-btn-primary" onClick={() => openModal()}>
            <Plus size={16} /> Add New Banner
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <AdminHeader title="Hero Banners" />
          <div className="ahb-page">
            {pageHeading(false)}
            <div className="ahb-state">
              <Loader2 size={26} className="ahb-spinner" />
              <p className="ahb-state-text">Loading hero banners...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Hero Banners" />
        <div className="ahb-page">
          {pageHeading(true)}

          {error && (
            <div className="ahb-error-message" role="alert">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {banners.length === 0 ? (
            <div className="ahb-state ahb-state--empty">
              <span className="ahb-state-icon"><ImagePlus size={20} /></span>
              <p className="ahb-state-title">No hero banners configured yet</p>
              <p className="ahb-state-text">Upload top homepage showcase banners to feature major promotions.</p>
              <button type="button" className="ahb-btn-primary" onClick={() => openModal()}>
                <Plus size={16} /> Add First Banner
              </button>
            </div>
          ) : (
            <Table columns={columns} data={banners} show />
          )}
        </div>

        {showModal && (
          <div className="ahb-modal-overlay" onClick={closeModal}>
            <div className="ahb-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="ahb-modal-head">
                <h2>{editingBanner ? 'Edit Banner' : 'Add New Banner'}</h2>
                <button type="button" className="ahb-modal-close" onClick={closeModal} aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="ahb-form">
                <div className="ahb-modal-body">
                  <div className="ahb-form-group">
                    <label>Title (big headline on the banner)</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="ahb-form-group">
                    <label>Subtitle (text inside the white button strip)</label>
                    <input
                      type="text"
                      value={formData.subtitle}
                      onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    />
                  </div>

                  <div className="ahb-form-group">
                    <label>Banner Image {editingBanner ? '(leave empty to keep current)' : ''}</label>
                    <div
                      className="ahb-dropzone"
                      onDragEnter={dragOn}
                      onDragLeave={dragOff}
                      onDrop={dragOff}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        ref={imageInputRef}
                        onChange={handleImageChange}
                        required={!editingBanner}
                        className="ahb-dropzone-input"
                        aria-label="Banner image"
                      />
                      <span className="ahb-dropzone-icon"><Upload size={18} /></span>
                      <span className="ahb-dropzone-text">
                        <span className="ahb-dropzone-title">
                          {imageFile ? imageFile.name : <><em>Click to upload</em> or drag an image here</>}
                        </span>
                        <span className="ahb-dropzone-hint">PNG, JPG or WebP</span>
                      </span>
                    </div>

                    {imagePreview && (
                      <div className="ahb-image-preview">
                        <img
                          src={imagePreview}
                          alt="Banner preview"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                          onLoad={(e) => {
                            e.currentTarget.style.display = 'block';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="ahb-form-group">
                    <label>View All</label>
                    <input
                      type="text"
                      value={formData.ctaPrimaryLabel}
                      onChange={(e) => setFormData({ ...formData, ctaPrimaryLabel: e.target.value })}
                      required
                    />
                  </div>

                  <div className="ahb-form-group">
                    <label>S.Order in List</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value, 10) })}
                      min="0"
                    />
                  </div>
                  <div className="ahb-checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      Active
                    </label>
                  </div>
                </div>
                <div className="ahb-modal-actions">
                  <button type="button" className="ahb-btn-cancel" onClick={closeModal}>Cancel</button>
                  <button type="submit" className="ahb-btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHeroBannersPage;
